/**
 * スクリーンショット取得サービス
 * 30秒間隔でカメラ画像のスクリーンショットを取得し、面接終了時に一括アップロード
 */

import { CircuitBreaker, createCircuitBreaker } from '../utils/circuitBreaker';
import { retry, RetryPresets } from '../utils/retryMechanism';
import { ImageOptimizer, OptimizationPresets } from '../utils/imageOptimizer';

/**
 * 撮影時刻をファイル名に使える "YYYYMMDD-HHmmss" 形式に整形する
 */
function formatTimestampForFilename(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  const datePart = `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
  const timePart = `${pad(date.getHours())}${pad(date.getMinutes())}${pad(date.getSeconds())}`;
  return `${datePart}-${timePart}`;
}

export interface ScreenshotOptions {
  camera: boolean;
  interval?: number; // スクリーンショット取得間隔（ミリ秒）
  quality?: number; // JPEG品質 (0.1 - 1.0)
  maxWidth?: number; // 画像の最大幅
  maxHeight?: number; // 画像の最大高さ
}

export interface ScreenshotState {
  isCapturing: boolean;
  screenshotCount: number;
  totalSize: number;
  lastCaptureTime?: Date;
}

export interface CapturedScreenshot {
  blob: Blob;
  timestamp: Date;
  filename: string;
}

export class ScreenshotCaptureService {
  private captureInterval: NodeJS.Timeout | null = null;
  private screenshots: CapturedScreenshot[] = [];
  private videoElement: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private canvas: HTMLCanvasElement;
  private canvasContext: CanvasRenderingContext2D;
  private startTime: number = 0;
  private screenshotStateCallback?: (state: ScreenshotState) => void;
  private currentOptions: ScreenshotOptions | null = null;

  // メモリ管理の設定
  private readonly MAX_MEMORY_USAGE = 50 * 1024 * 1024; // 50MB上限
  private readonly MAX_SCREENSHOTS = 100; // 最大100枚
  private readonly CLEANUP_THRESHOLD = 0.8; // 80%でクリーンアップ開始
  private currentMemoryUsage = 0;
  private cleanupInProgress = false;

  // エラー回復メカニズム
  private cameraCircuitBreaker: CircuitBreaker;
  private uploadCircuitBreaker: CircuitBreaker;

  // 画像最適化
  private imageOptimizer: ImageOptimizer;

  constructor(onStateChange?: (state: ScreenshotState) => void) {
    this.screenshotStateCallback = onStateChange;

    // Canvas要素を作成（非表示）
    this.canvas = document.createElement('canvas');
    this.canvas.style.display = 'none';
    document.body.appendChild(this.canvas);

    const context = this.canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2Dコンテキストの取得に失敗しました');
    }
    this.canvasContext = context;

    // Circuit Breakerの初期化
    this.cameraCircuitBreaker = createCircuitBreaker({
      failureThreshold: 3, // 3回失敗で遮断
      recoveryTimeout: 30000, // 30秒待機
      successThreshold: 2, // 2回成功で復旧
    });

    this.uploadCircuitBreaker = createCircuitBreaker({
      failureThreshold: 5, // 5回失敗で遮断
      recoveryTimeout: 60000, // 60秒待機
      successThreshold: 3, // 3回成功で復旧
    });

    // 画像最適化器の初期化
    this.imageOptimizer = new ImageOptimizer();
  }

  /**
   * スクリーンショット取得を開始
   */
  async startCapture(options: ScreenshotOptions, videoElement: HTMLVideoElement): Promise<void> {
    try {
      // 必須パラメータのバリデーション
      if (!videoElement) {
        throw new Error('Video要素が存在しません');
      }

      if (!options.camera) {
        throw new Error('カメラオプションが必要です');
      }

      if (this.captureInterval) {
        await this.stopCapture();
      }

      this.currentOptions = {
        interval: 30000, // デフォルト30秒
        quality: 0.8, // デフォルト品質80%
        maxWidth: 640, // デフォルト幅
        maxHeight: 480, // デフォルト高さ
        ...options,
      };

      this.videoElement = videoElement;

      // カメラストリームを取得（Circuit Breaker + Retry）
      this.stream = await this.cameraCircuitBreaker.execute(() =>
        retry(() => this.getCameraStreamInternal(), RetryPresets.CAMERA)
      );

      if (this.videoElement) {
        this.videoElement.srcObject = this.stream;

        this.videoElement.addEventListener('error', (e) => {
          console.error('Video error:', e);
        });
      }

      // Canvas サイズを設定
      this.canvas.width = this.currentOptions.maxWidth!;
      this.canvas.height = this.currentOptions.maxHeight!;

      // 初期化
      this.screenshots = [];
      this.currentMemoryUsage = 0;
      this.startTime = Date.now();

      // Video要素のストリーム設定を待機
      await this.waitForVideoReady();

      setTimeout(() => {
        this.captureScreenshot();
      }, 3000);

      this.captureInterval = setInterval(() => {
        this.captureScreenshot();
      }, this.currentOptions.interval);

      this.updateState();
    } catch (error) {
      console.error('Screenshot capture start error:', error);
      throw error;
    }
  }

  /**
   * 単一のスクリーンショットを取得
   */
  private async captureScreenshot(): Promise<void> {
    try {
      if (!this.videoElement || !this.currentOptions) {
        console.warn('VideoElement or Options not set');
        return;
      }

      // ビデオが再生中でない場合はスキップ
      if (this.videoElement.readyState < 2) {
        console.warn('Video not ready');
        return;
      }

      // Canvasにビデオフレームを描画
      this.canvasContext.drawImage(this.videoElement, 0, 0, this.canvas.width, this.canvas.height);

      // Canvasから画像をBlob として取得
      const blob = await new Promise<Blob | null>((resolve) => {
        this.canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              console.error('Blob creation failed');
              resolve(null);
            }
          },
          'image/jpeg',
          this.currentOptions!.quality
        );
      });

      if (blob) {
        const timestamp = new Date();
        const filename = `screenshot_${String(this.screenshots.length + 1).padStart(3, '0')}_${formatTimestampForFilename(timestamp)}.jpg`;

        let optimizedBlob = blob;
        try {
          const optimizationResult = await this.imageOptimizer.optimize(
            blob,
            OptimizationPresets.INTERVIEW_SCREENSHOT
          );

          optimizedBlob = optimizationResult.optimizedBlob;
        } catch (optimizationError) {
          console.warn('Image optimization failed, using original:', optimizationError);
        }

        const capturedScreenshot: CapturedScreenshot = {
          blob: optimizedBlob,
          timestamp,
          filename,
        };

        // メモリ制限チェック
        const canAddScreenshot = await this.checkMemoryLimits();

        if (canAddScreenshot) {
          this.screenshots.push(capturedScreenshot);
        } else {
          if (process.env.NODE_ENV === 'development') {
            console.warn('Memory limit reached, screenshot skipped');
          }
        }

        this.updateState();
      }
    } catch (error) {
      console.error('Screenshot capture error:', error);
    }
  }

  /**
   * スクリーンショット取得を停止
   */
  async stopCapture(): Promise<CapturedScreenshot[]> {
    try {
      // 最後のスクリーンショットを取得
      if (this.videoElement && this.currentOptions) {
        await this.captureScreenshot();
      }

      // タイマーを停止
      if (this.captureInterval) {
        clearInterval(this.captureInterval);
        this.captureInterval = null;
      }

      this.cleanupStream();

      this.updateState();

      return [...this.screenshots];
    } catch (error) {
      console.error('Screenshot stop error:', error);
      return [];
    }
  }

  /**
   * 取得をキャンセル
   */
  cancelCapture(): void {
    if (this.captureInterval) {
      clearInterval(this.captureInterval);
      this.captureInterval = null;
    }
    this.screenshots = [];
    this.currentMemoryUsage = 0;
    this.cleanupStream();
    this.updateState();
  }

  /**
   * Video要素の準備完了を待機
   */
  private async waitForVideoReady(): Promise<void> {
    const MAX_WAIT_TIME = 10000; // 最大10秒待機
    const CHECK_INTERVAL = 100; // 100ms間隔でチェック

    return new Promise((resolve, reject) => {
      const startTime = Date.now();

      const checkVideoReady = () => {
        if (!this.videoElement) {
          reject(new Error('Video要素が存在しません'));
          return;
        }

        const isReady =
          this.videoElement.readyState >= 1 && // HAVE_METADATA = 1
          this.videoElement.videoWidth > 0 &&
          this.videoElement.videoHeight > 0 &&
          this.videoElement.srcObject !== null;

        if (isReady) {
          resolve();
          return;
        }

        // タイムアウトチェック
        if (Date.now() - startTime > MAX_WAIT_TIME) {
          reject(new Error('Video要素の準備タイムアウト'));
          return;
        }

        // 次のチェックをスケジュール
        setTimeout(checkVideoReady, CHECK_INTERVAL);
      };

      checkVideoReady();
    });
  }

  /**
   * カメラストリームを取得（内部実装）
   */
  private async getCameraStreamInternal(): Promise<MediaStream> {
    try {
      // MediaDevicesが利用可能かチェック
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('お使いのブラウザでカメラ機能がサポートされていません');
      }

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter((device) => device.kind === 'videoinput');

      if (videoDevices.length === 0) {
        throw new Error('カメラデバイスが見つかりません');
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: this.currentOptions?.maxWidth || 640 },
          height: { ideal: this.currentOptions?.maxHeight || 480 },
          facingMode: 'user',
        },
        audio: false,
      });

      return stream;
    } catch (error: any) {
      console.error('Camera access error:', error);

      if (error.name === 'NotAllowedError') {
        throw new Error(
          'カメラの使用が許可されていません。ブラウザの設定でカメラのアクセスを許可してください。'
        );
      } else if (error.name === 'NotFoundError') {
        throw new Error(
          'カメラデバイスが見つかりません。カメラが接続されているか確認してください。'
        );
      } else if (error.name === 'NotReadableError') {
        throw new Error('カメラが他のアプリケーションで使用中の可能性があります。');
      } else if (error.name === 'OverconstrainedError') {
        throw new Error('指定されたカメラ設定がサポートされていません。');
      } else if (error.name === 'SecurityError') {
        throw new Error(
          'セキュリティ上の理由でカメラにアクセスできません。HTTPSが必要な場合があります。'
        );
      } else if (error.message) {
        throw new Error(`カメラエラー: ${error.message}`);
      } else {
        throw new Error('カメラの初期化に失敗しました。ページを再読み込みしてお試しください。');
      }
    }
  }

  /**
   * 経過時間を取得（秒）
   */
  getDuration(): number {
    if (!this.startTime) return 0;
    return Math.floor((Date.now() - this.startTime) / 1000);
  }

  /**
   * 総サイズを取得（バイト）
   */
  getTotalSize(): number {
    return this.screenshots.reduce((total, screenshot) => total + screenshot.blob.size, 0);
  }

  /**
   * スクリーンショット数を取得
   */
  getScreenshotCount(): number {
    return this.screenshots.length;
  }

  /**
   * 現在の状態を取得
   */
  getState(): ScreenshotState {
    return {
      isCapturing: this.captureInterval !== null,
      screenshotCount: this.getScreenshotCount(),
      totalSize: this.getTotalSize(),
      lastCaptureTime:
        this.screenshots.length > 0
          ? this.screenshots[this.screenshots.length - 1].timestamp
          : undefined,
    };
  }

  /**
   * 取得したスクリーンショットを取得
   */
  getScreenshots(): CapturedScreenshot[] {
    return [...this.screenshots];
  }

  /**
   * 状態を更新
   */
  private updateState(): void {
    if (this.screenshotStateCallback) {
      this.screenshotStateCallback(this.getState());
    }
  }

  /**
   * ストリームをクリーンアップ
   */
  private cleanupStream(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.videoElement) {
      this.videoElement.srcObject = null;
    }
  }

  /**
   * リソースを解放
   */
  dispose(): void {
    this.cancelCapture();

    // Canvas要素を削除
    if (this.canvas && this.canvas.parentNode) {
      this.canvas.parentNode.removeChild(this.canvas);
    }

    // 画像最適化器のクリーンアップ
    if (this.imageOptimizer) {
      this.imageOptimizer.dispose();
    }

    this.screenshotStateCallback = undefined;
  }

  /**
   * ファイルサイズをフォーマット
   */
  static formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * 時間をフォーマット
   */
  static formatDuration(seconds: number): string {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  }

  /**
   * メモリ使用量を更新
   */
  private updateMemoryUsage(): void {
    this.currentMemoryUsage = this.screenshots.reduce(
      (total, screenshot) => total + screenshot.blob.size,
      0
    );
  }

  /**
   * メモリ使用量の確認
   */
  getMemoryUsage(): { used: number; max: number; percentage: number; formatted: string } {
    const used = this.currentMemoryUsage;
    const percentage = (used / this.MAX_MEMORY_USAGE) * 100;

    return {
      used,
      max: this.MAX_MEMORY_USAGE,
      percentage: Math.round(percentage * 100) / 100,
      formatted: `${ScreenshotCaptureService.formatFileSize(used)} / ${ScreenshotCaptureService.formatFileSize(this.MAX_MEMORY_USAGE)}`,
    };
  }

  /**
   * 古いスクリーンショットのクリーンアップ
   */
  private async cleanupOldScreenshots(): Promise<void> {
    if (this.cleanupInProgress) return;

    this.cleanupInProgress = true;

    try {
      console.log('🧹 メモリクリーンアップ開始:', {
        beforeCount: this.screenshots.length,
        beforeMemory: ScreenshotCaptureService.formatFileSize(this.currentMemoryUsage),
      });

      // 古いスクリーンショットから削除（最初に取得したものから削除）
      const targetCount = Math.floor(this.MAX_SCREENSHOTS * 0.7); // 70%まで削減
      const toRemove = this.screenshots.length - targetCount;

      if (toRemove > 0) {
        const removedScreenshots = this.screenshots.splice(0, toRemove);

        // メモリ使用量を再計算
        this.updateMemoryUsage();

        console.log('🧹 メモリクリーンアップ完了:', {
          removedCount: removedScreenshots.length,
          afterCount: this.screenshots.length,
          afterMemory: ScreenshotCaptureService.formatFileSize(this.currentMemoryUsage),
          freedMemory: ScreenshotCaptureService.formatFileSize(
            removedScreenshots.reduce((total, shot) => total + shot.blob.size, 0)
          ),
        });

        // 状態更新を通知
        this.updateState();
      }
    } catch (error) {
      console.error('❌ メモリクリーンアップエラー:', error);
    } finally {
      this.cleanupInProgress = false;
    }
  }

  /**
   * メモリ制限チェックと自動クリーンアップ
   */
  private async checkMemoryLimits(): Promise<boolean> {
    this.updateMemoryUsage();

    // スクリーンショット数制限チェック
    if (this.screenshots.length >= this.MAX_SCREENSHOTS) {
      console.warn('⚠️ 最大スクリーンショット数に達しました。クリーンアップを実行します。');
      await this.cleanupOldScreenshots();
      return false; // 新しいスクリーンショットの追加をスキップ
    }

    // メモリ使用量制限チェック
    const memoryUsageRatio = this.currentMemoryUsage / this.MAX_MEMORY_USAGE;

    if (memoryUsageRatio >= 1.0) {
      console.warn('⚠️ メモリ使用量上限に達しました。クリーンアップを実行します。');
      await this.cleanupOldScreenshots();
      return false; // 新しいスクリーンショットの追加をスキップ
    }

    if (memoryUsageRatio >= this.CLEANUP_THRESHOLD) {
      console.warn(
        `⚠️ メモリ使用量が${Math.round(memoryUsageRatio * 100)}%に達しました。予防的クリーンアップを実行します。`
      );
      await this.cleanupOldScreenshots();
    }

    return true; // 新しいスクリーンショットの追加を許可
  }

  async forceCleanup(): Promise<void> {
    const beforeCount = this.screenshots.length;
    const beforeMemory = this.currentMemoryUsage;

    this.screenshots = [];
    this.currentMemoryUsage = 0;

    if (process.env.NODE_ENV === 'development') {
      console.log('Force memory cleanup completed:', {
        removedCount: beforeCount,
        freedMemory: ScreenshotCaptureService.formatFileSize(beforeMemory),
      });
    }

    this.updateState();
  }

  /**
   * Circuit Breaker統計情報の取得
   */
  getCircuitBreakerStats(): {
    camera: any;
    upload: any;
  } {
    return {
      camera: this.cameraCircuitBreaker.getStats(),
      upload: this.uploadCircuitBreaker.getStats(),
    };
  }

  resetCircuitBreakers(): void {
    this.cameraCircuitBreaker.manualReset();
    this.uploadCircuitBreaker.manualReset();
  }

  /**
   * システム状態の詳細情報を取得
   */
  getSystemHealth(): {
    memory: any;
    circuitBreakers: any;
    screenshots: any;
    camera: any;
  } {
    return {
      memory: this.getMemoryUsage(),
      circuitBreakers: this.getCircuitBreakerStats(),
      screenshots: {
        count: this.screenshots.length,
        totalSize: this.getTotalSize(),
        lastCaptureTime:
          this.screenshots.length > 0
            ? this.screenshots[this.screenshots.length - 1].timestamp
            : null,
      },
      camera: {
        isActive: this.stream !== null,
        isCapturing: this.captureInterval !== null,
        trackCount: this.stream?.getTracks().length || 0,
      },
    };
  }
}
